/**
 * The register's realtime client (T07-40, R21, ADR-0010, design/07 §9): **one** `@tcg/realtime`
 * client per tab carrying three connections behind the same `subscribe()`:
 *
 * | connection | topics | what it brings |
 * |---|---|---|
 * | realtime hub `GET /api/v1/realtime/stream` | `pos:register:<id>` | "something changed" — sales, the drawer (shift / cash movements / payments), 取り置き that ran out, 免税 records, prices, POS rules, this tablet paired / revoked; web **pickup** orders ready / picked up / cancelled (every register, `hint.locationId` = the shop — T14-15; filtered in `realtime/pickups.ts`) → the screens refetch |
 * | inventory-service `GET /api/v1/pos/inventory/stream` (T03-17) | `inventory.changed:<variantId>` | availability **with the payload at once** — a card on screen sold on the web / at another register → the sell button closes without a refetch |
 * | pos-service `GET /api/v1/pos/events/stream` (T07-06) | `pos.events:*` | the basket, holds, sales, 買取 of this register (payload at once) |
 *
 * The bearer is the signed-in staff member's PIN token (register claim + `inventory.read` of the
 * PIN subset), else the device token — the register topic needs no `orders.read` (T07-42). The inventory stream is scoped to
 * the register and its location (quantities of this shop, not the web total): both come from
 * {@link RealtimeScope}, filled from `GET /pos/registers/current` before any variant topic is
 * subscribed.
 */
import {
  createRealtimeClient,
  inventoryStream,
  type InventoryStreamOptions,
  type RealtimeClient,
} from "@tcg/realtime";

import { posEventsStream } from "@/lib/sse";

/** Register + location the inventory stream is scoped to (set once the register is known). */
export class RealtimeScope {
  registerId: string | null = null;
  locationId: string | null = null;

  /** The layout tells it every render (cheap; read when a stream (re)connects). */
  set(registerId: string | null, locationId: string | null): void {
    this.registerId = registerId;
    this.locationId = locationId;
  }
}

export function createRealtimeScope(): RealtimeScope {
  return new RealtimeScope();
}

export interface PosRealtimeOptions {
  /** PIN token while signed in, else the device token (`AuthController.bearer`). */
  bearer: () => Promise<string | null>;
  scope: RealtimeScope;
  fetch?: typeof fetch;
  /** Gateway prefix of the hub (default `/api/v1/realtime`). */
  hubBaseUrl?: string;
  /** Gateway path of inventory-service's stream (default `/api/v1/pos/inventory/stream`). */
  inventoryPath?: string;
  /** Gateway path of pos-service's stream (default `/api/v1/pos/events/stream`). */
  posEventsPath?: string;
  /** `false` = no hub (only the per-service streams). */
  hub?: boolean;
  /** Tests: do not watch document / window events. */
  watchPage?: boolean;
}

/** The inventory stream reads the scope when it (re)connects — never a stale register. */
export function scopedInventoryOptions(
  scope: RealtimeScope,
  path: string | undefined,
): InventoryStreamOptions {
  return {
    ...(path ? { path } : {}),
    get registerId() {
      return scope.registerId;
    },
    get locationId() {
      return scope.locationId;
    },
  };
}

export function createPosRealtime(options: PosRealtimeOptions): RealtimeClient {
  return createRealtimeClient({
    hub: options.hub ?? true,
    ...(options.hubBaseUrl ? { baseUrl: options.hubBaseUrl } : {}),
    // the bearer goes in a header (a PIN token must never sit in a URL / proxy log)
    transport: "fetch",
    getToken: () => options.bearer(),
    streams: [
      inventoryStream(scopedInventoryOptions(options.scope, options.inventoryPath)),
      posEventsStream(options.posEventsPath ? { path: options.posEventsPath } : {}),
    ],
    ...(options.fetch ? { fetch: options.fetch } : {}),
    ...(options.watchPage === false ? { watchPage: false } : {}),
  });
}
